import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { getCatalog, grantBotCards, revokeBotCards } from '../../api/admin';
import { useAsync } from '../../api/useAsync';
import { Modal, Pill } from '../../components/ui';
import type { OwnedCard } from '../../types/admin';

export function BotCardsPanel({
  botId,
  cards,
  onChanged,
}: {
  botId: string;
  cards: OwnedCard[];
  onChanged: () => void;
}): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [granting, setGranting] = useState(false);

  const sorted = useMemo(
    () => [...cards].sort((a, b) => (a.name ?? a.cardId).localeCompare(b.name ?? b.cardId)),
    [cards],
  );

  const revoke = async (instanceId: string): Promise<void> => {
    setBusyId(instanceId);
    setError(null);
    try {
      await revokeBotCards(botId, [instanceId]);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível remover a carta.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="panel">
      <header className="panel__header">
        <h2 className="panel__title">Cartas ({cards.length})</h2>
        <button type="button" className="btn btn--primary" onClick={() => setGranting(true)}>
          Adicionar cartas
        </button>
      </header>

      {error ? <div className="form-error">{error}</div> : null}

      <div className="card-grid">
        {sorted.map((card) => {
          const inDeck = card.inDeckIds.length > 0;
          return (
            <div className="card-tile" key={card.instanceId}>
              {card.artUrl ? (
                <img className="card-tile__art" src={card.artUrl} alt="" loading="lazy" />
              ) : (
                <div className="card-tile__art card-tile__art--empty" />
              )}
              <div className="card-tile__body">
                <div className="card-tile__name">{card.name ?? card.cardId}</div>
                <div className="card-tile__meta">
                  {card.cost !== null ? <Pill>custo {card.cost}</Pill> : null}
                  {card.missingFromCatalog ? <Pill tone="danger">fora do catálogo</Pill> : null}
                  {inDeck ? <Pill tone="accent">no deck</Pill> : null}
                </div>
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  disabled={inDeck || busyId === card.instanceId}
                  // The server refuses this too (409 INSTANCE_IN_DECK) — removing
                  // a pinned card would make the deck unresolvable mid-match.
                  title={
                    inDeck
                      ? 'Remova a carta do deck antes de excluí-la'
                      : 'Remover do inventário do bot'
                  }
                  onClick={() => void revoke(card.instanceId)}
                >
                  {busyId === card.instanceId ? '…' : 'Remover'}
                </button>
              </div>
            </div>
          );
        })}
        {cards.length === 0 ? <div className="empty-state">Este bot não tem cartas.</div> : null}
      </div>

      {granting ? (
        <GrantCardsModal
          botId={botId}
          onClose={() => setGranting(false)}
          onGranted={() => {
            setGranting(false);
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}

function GrantCardsModal({
  botId,
  onClose,
  onGranted,
}: {
  botId: string;
  onClose: () => void;
  onGranted: () => void;
}): React.JSX.Element {
  const load = useCallback(() => getCatalog(), []);
  const { data, loading, error } = useAsync(load);
  const [selected, setSelected] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const visible = useMemo(() => {
    const list = data ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (c) => c.name.toLowerCase().includes(q) || c.cardId.toLowerCase().includes(q),
    );
  }, [data, filter]);

  const toggle = (cardId: string): void => {
    setSelected((prev) =>
      prev.includes(cardId) ? prev.filter((c) => c !== cardId) : [...prev, cardId],
    );
  };

  const submit = async (): Promise<void> => {
    if (selected.length === 0) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await grantBotCards(botId, selected);
      onGranted();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Não foi possível adicionar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Adicionar cartas" onClose={onClose} wide>
      {/* Cards are compiled classes in the game server — the panel can only
          hand out copies of cards that already exist in the catalog. */}
      <p className="field__hint">
        Escolha cartas do catálogo do jogo. Cada seleção cria uma nova cópia no inventário do
        bot. Não é possível criar cartas novas por aqui — elas fazem parte do código do jogo.
      </p>

      <input
        className="search"
        placeholder="Filtrar catálogo…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        autoFocus
      />

      {loading ? <div className="page-loading">Carregando catálogo…</div> : null}
      {error ? <div className="form-error">{error.message}</div> : null}

      <div className="catalog-grid">
        {visible.map((card) => {
          const count = selected.filter((c) => c === card.cardId).length;
          return (
            <button
              type="button"
              key={card.cardId}
              className={`catalog-tile${count > 0 ? ' catalog-tile--selected' : ''}`}
              onClick={() => toggle(card.cardId)}
            >
              {card.artUrl ? (
                <img className="catalog-tile__art" src={card.artUrl} alt="" loading="lazy" />
              ) : (
                <div className="catalog-tile__art catalog-tile__art--empty" />
              )}
              <span className="catalog-tile__name">{card.name}</span>
              <span className="catalog-tile__cost">{card.cost}</span>
            </button>
          );
        })}
      </div>

      {submitError ? <div className="form-error">{submitError}</div> : null}

      <div className="modal__actions">
        <span className="modal__actions-note">{selected.length} selecionada(s)</span>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={selected.length === 0 || busy}
          onClick={() => void submit()}
        >
          {busy ? 'Adicionando…' : 'Adicionar'}
        </button>
      </div>
    </Modal>
  );
}
